import { useQuery } from 'react-query';
import { archivosFetch } from '../../utils/fetch';
import { DocumentFilters, DocumentsPage } from './types';

// Server-side paginated listing.
//
// Two endpoints depending on the "Archiveur" filter:
//   - empty (all)       → GET /api/archives/documents
//                         The backend trims the confidential ones of others with
//                         `listingScope`; the screen filters nothing.
//   - a user            → GET /api/archives/:id/my-documents
//                         `requireAdminOrSelf`: a USER can only request their
//                         own, an ADMIN anyone's. It is the only filter
//                         by archiver that the API offers: `searchIssuer`
//                         filters by `issuer`, which is another thing. `searchIssuer`
//                         is not sent here because, if it is, the handler stops
//                         narrowing by archiver.
//
// The dates travel as a range (`YYYY-MM-DD` bounds, either optional on its own): it is
// what the API filters by.

export const HOME_DOCUMENTS_KEY = 'HOME_DOCUMENTS';

type Params = {
  filters: DocumentFilters;
  page: number;
  pageSize: number;
};

const buildUrl = ({ filters, page, pageSize }: Params): string => {
  const params = new URLSearchParams({
    page: String(page + 1),
    limit: String(pageSize),
  });

  if (filters.search) params.set('searchText', filters.search);
  if (filters.service) params.set('searchService', filters.service);
  if (filters.issuingDateFrom) {
    params.set('searchDateFrom', filters.issuingDateFrom.format('YYYY-MM-DD'));
  }
  if (filters.issuingDateTo) {
    params.set('searchDateTo', filters.issuingDateTo.format('YYYY-MM-DD'));
  }
  if (filters.archivingDateFrom) {
    params.set(
      'searchArchivingDateFrom',
      filters.archivingDateFrom.format('YYYY-MM-DD'),
    );
  }
  if (filters.archivingDateTo) {
    params.set(
      'searchArchivingDateTo',
      filters.archivingDateTo.format('YYYY-MM-DD'),
    );
  }

  const path = filters.archiver
    ? `/api/archives/${encodeURIComponent(filters.archiver)}/my-documents`
    : '/api/archives/documents';

  return `${process.env.REACT_APP_API_URL}${path}?${params.toString()}`;
};

const fetchDocuments = async (params: Params): Promise<DocumentsPage> => {
  const response = await archivosFetch(buildUrl(params));

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const body = await response.json();

  return {
    documents: Array.isArray(body?.documents) ? body.documents : [],
    totalItems: typeof body?.totalItems === 'number' ? body.totalItems : 0,
  };
};

export const useDocuments = (params: Params) => {
  const { filters, page, pageSize } = params;

  return useQuery<DocumentsPage, Error>(
    [
      HOME_DOCUMENTS_KEY,
      filters.archiver,
      filters.service,
      filters.search,
      filters.issuingDateFrom?.format('YYYY-MM-DD') ?? '',
      filters.issuingDateTo?.format('YYYY-MM-DD') ?? '',
      filters.archivingDateFrom?.format('YYYY-MM-DD') ?? '',
      filters.archivingDateTo?.format('YYYY-MM-DD') ?? '',
      page,
      pageSize,
    ],
    () => fetchDocuments(params),
    {
      // When changing page or filter the table keeps the previous
      // rows until the new ones arrive, instead of emptying.
      keepPreviousData: true,
      retry: false,
    },
  );
};
